import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, X, Share, Plus } from "lucide-react";

const DISMISS_KEY = "ghost_install_dismissed";

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;

const isIOS = () =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;

export const InstallPrompt = () => {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(DISMISS_KEY)) return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    // iOS Safari never fires beforeinstallprompt — show manual hint instead
    if (isIOS()) setShow(true);

    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const dismiss = () => {
    setShow(false);
    setIosHint(false);
    localStorage.setItem(DISMISS_KEY, "1");
  };

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      await deferred.userChoice;
      setDeferred(null);
      dismiss();
    } else if (isIOS()) {
      setIosHint((v) => !v);
    }
  };

  if (!show) return null;

  return (
    <div data-testid="install-banner" className="rounded-lg border border-[#0055FF]/30 bg-[#0055FF]/5 p-3">
      <div className="flex items-center gap-3">
        <div className="h-9 w-9 rounded-md bg-[#0A0A0A] grid place-items-center shrink-0">
          <img src="/ghost-logo.png" alt="Ghost TMS" className="h-9 w-9 object-cover rounded-md" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold leading-tight">Install Ghost TMS</div>
          <div className="text-xs text-muted-foreground">Add to your home screen for one-tap access.</div>
        </div>
        <Button size="sm" data-testid="install-app-btn" onClick={install} className="gap-1.5 bg-[#0055FF] hover:bg-[#0044CC] shrink-0">
          <Download className="h-4 w-4" /> Install
        </Button>
        <button data-testid="install-dismiss-btn" onClick={dismiss} className="text-muted-foreground hover:text-foreground p-1.5 shrink-0" title="Dismiss">
          <X className="h-4 w-4" />
        </button>
      </div>
      {iosHint && (
        <div data-testid="install-ios-hint" className="mt-2 pt-2 border-t border-[#0055FF]/20 text-xs text-neutral-700 flex items-center gap-1.5 flex-wrap">
          <span>In Safari, tap</span>
          <Share className="h-3.5 w-3.5 text-[#0055FF]" />
          <span className="font-medium">Share</span>
          <span>then</span>
          <Plus className="h-3.5 w-3.5 text-[#0055FF]" />
          <span className="font-medium">Add to Home Screen</span>.
        </div>
      )}
    </div>
  );
};
